/**
 * Supabase: saloninstellingen, abonnement, lookup en activity categories.
 */

import { supabase, DEFAULT_SALON_ID } from '../supabase'
import { slugify } from '../../lib/utils'
import { normalizeAnnuleringsbeleidForSave } from '../../lib/annuleringsbeleidHtml'
import type {
  SalonSettings,
  SalonAbonnement,
  BookingDepositMode,
  BookingDepositSettings,
} from '../../types/salon'
import { normalizeBusinessHours } from './utils'
import { resolveSalonSubscriptionStatus } from '../../features/abonnement/subscriptionStatus.utils'

export async function getSalonSettings(salonId = DEFAULT_SALON_ID): Promise<SalonSettings> {
  const { data, error } = await supabase
    .from('salons')
    .select('*')
    .eq('id', salonId)
    .maybeSingle()
  if (error) throw error
  if (!data) return {}
  const addressStreet = data.address_street ?? ''
  const postcode = data.postcode ?? ''
  const city = data.city ?? ''
  const address =
    addressStreet || postcode || city
      ? [addressStreet, [postcode, city].filter(Boolean).join(' ')].filter(Boolean).join(', ')
      : (data.address ?? '')
  return {
    businessName: data.business_name ?? '',
    slug: data.slug ?? null,
    businessOwnerEmail: data.business_owner_email ?? '',
    businessOwnerVoornaam: data.business_owner_voornaam ?? '',
    businessOwnerAchternaam: data.business_owner_achternaam ?? '',
    businessOwnerPhone: data.business_owner_phone ?? '',
    address,
    addressStreet,
    postcode,
    city,
    instagram: data.instagram ?? '',
    facebook: data.facebook ?? '',
    website: data.website ?? '',
    tiktok: data.tiktok ?? '',
    businessHours: normalizeBusinessHours(data.business_hours),
    bannerColor: data.banner_color ?? '',
    bannerImageUrl: data.banner_image_url ?? '',
    bookingLeadTime: (data.booking_lead_time as SalonSettings['bookingLeadTime']) ?? undefined,
    bookingAdvanceMax: (data.booking_advance_max as SalonSettings['bookingAdvanceMax']) ?? undefined,
    cancelRescheduleCutoff: (data.cancel_reschedule_cutoff as SalonSettings['cancelRescheduleCutoff']) ?? undefined,
    listOnMarketplace: (data as { list_on_marketplace?: boolean }).list_on_marketplace === true,
    annuleringsbeleid: (data as { annuleringsbeleid?: string | null }).annuleringsbeleid ?? '',
  }
}

export async function updateSalonSettings(
  data: Partial<SalonSettings>,
  salonId = DEFAULT_SALON_ID
): Promise<void> {
  const payload: Record<string, unknown> = {}
  if (data.businessName !== undefined) {
    payload.business_name = data.businessName
  }
  if (data.slug !== undefined) {
    payload.slug = data.slug ? slugify(String(data.slug)) : null
  }
  if (data.businessOwnerEmail !== undefined) payload.business_owner_email = data.businessOwnerEmail
  if (data.businessOwnerVoornaam !== undefined) payload.business_owner_voornaam = data.businessOwnerVoornaam || null
  if (data.businessOwnerAchternaam !== undefined) payload.business_owner_achternaam = data.businessOwnerAchternaam || null
  if (data.businessOwnerPhone !== undefined) payload.business_owner_phone = data.businessOwnerPhone
  if (data.address !== undefined) payload.address = data.address
  if (data.addressStreet !== undefined) payload.address_street = data.addressStreet
  if (data.postcode !== undefined) payload.postcode = data.postcode
  if (data.city !== undefined) payload.city = data.city
  if (data.addressStreet !== undefined || data.postcode !== undefined || data.city !== undefined) {
    const street = data.addressStreet ?? ''
    const pc = data.postcode ?? ''
    const c = data.city ?? ''
    payload.address = [street, [pc, c].filter(Boolean).join(' ')].filter(Boolean).join(', ') || null
  }
  if (data.instagram !== undefined) payload.instagram = data.instagram
  if (data.facebook !== undefined) payload.facebook = data.facebook
  if (data.website !== undefined) payload.website = data.website
  if (data.tiktok !== undefined) payload.tiktok = data.tiktok
  if (data.businessHours !== undefined) payload.business_hours = data.businessHours
  if (data.bannerColor !== undefined) payload.banner_color = data.bannerColor || null
  if (data.bannerImageUrl !== undefined) payload.banner_image_url = data.bannerImageUrl || null
  if (data.bookingLeadTime !== undefined) payload.booking_lead_time = data.bookingLeadTime || null
  if (data.bookingAdvanceMax !== undefined) payload.booking_advance_max = data.bookingAdvanceMax || null
  if (data.cancelRescheduleCutoff !== undefined) payload.cancel_reschedule_cutoff = data.cancelRescheduleCutoff || null
  if (data.listOnMarketplace !== undefined) payload.list_on_marketplace = data.listOnMarketplace
  if (data.annuleringsbeleid !== undefined) {
    const normalized = normalizeAnnuleringsbeleidForSave(data.annuleringsbeleid)
    payload.annuleringsbeleid = normalized || null
  }
  if (Object.keys(payload).length === 0) return
  const { error } = await supabase.from('salons').update(payload).eq('id', salonId)
  if (error) throw error
}

const LEGACY_ABONNEMENT: SalonAbonnement = {
  trialEndsAt: null,
  abonnementBevestigdAt: null,
  modulePlanning: true,
  moduleKassa: false,
  moduleBetaling: false,
  onlineBetalenBijBoeken: false,
  subscriptionStatus: 'trialing',
  accessStatus: 'trialing',
  graceUntil: null,
  currentPeriodEnd: null,
}

const ABONNEMENT_SELECT =
  'trial_ends_at, abonnement_bevestigd_at, module_planning, module_kassa, module_betaling, online_betalen_bij_boeken, subscription_status'
const ABONNEMENT_SELECT_LEGACY =
  'trial_ends_at, abonnement_bevestigd_at, module_planning, module_kassa, module_betaling, subscription_status'

type SalonAbonnementRow = {
  trial_ends_at?: string | null
  abonnement_bevestigd_at?: string | null
  module_planning?: boolean | null
  module_kassa?: boolean | null
  module_betaling?: boolean | null
  online_betalen_bij_boeken?: boolean | null
  subscription_status?: string | null
}

function mapSalonAbonnement (
  data: SalonAbonnementRow,
  subRow: { status?: string | null; grace_until?: string | null; current_period_end?: string | null } | null,
  onlineBetalenFallbackToModule: boolean
): SalonAbonnement {
  const salonCache = data.subscription_status
  const accessStatus = resolveSalonSubscriptionStatus(salonCache, null)
  const subscriptionStatus = resolveSalonSubscriptionStatus(salonCache, subRow?.status as string | null)
  const moduleBetaling = Boolean(data.module_betaling)
  return {
    trialEndsAt: data.trial_ends_at ?? null,
    abonnementBevestigdAt: data.abonnement_bevestigd_at ?? null,
    modulePlanning: Boolean(data.module_planning),
    moduleKassa: Boolean(data.module_kassa),
    moduleBetaling,
    onlineBetalenBijBoeken: onlineBetalenFallbackToModule
      ? moduleBetaling
      : Boolean(data.online_betalen_bij_boeken),
    subscriptionStatus,
    accessStatus,
    graceUntil: subRow?.grace_until ?? null,
    currentPeriodEnd: subRow?.current_period_end ?? null,
  }
}

function isMissingColumnError (error: { code?: string; message?: string; status?: number }): boolean {
  const msg = error.message ?? ''
  return error.code === 'PGRST204' || msg.includes('column') || error.status === 400
}

export async function getSalonAbonnement(salonId = DEFAULT_SALON_ID): Promise<SalonAbonnement | null> {
  let data: SalonAbonnementRow | null = null
  let onlineBetalenFallbackToModule = false

  const first = await supabase
    .from('salons')
    .select(ABONNEMENT_SELECT)
    .eq('id', salonId)
    .maybeSingle()

  if (first.error) {
    if (!isMissingColumnError(first.error)) throw first.error
    const retry = await supabase
      .from('salons')
      .select(ABONNEMENT_SELECT_LEGACY)
      .eq('id', salonId)
      .maybeSingle()
    if (retry.error) {
      if (isMissingColumnError(retry.error)) {
        const { data: exists } = await supabase.from('salons').select('id').eq('id', salonId).maybeSingle()
        return exists ? LEGACY_ABONNEMENT : null
      }
      throw retry.error
    }
    data = retry.data
    onlineBetalenFallbackToModule = true
  } else {
    data = first.data
  }

  if (!data) return null

  const { data: subRow } = await supabase
    .from('subscriptions')
    .select('status, grace_until, current_period_end')
    .eq('salon_id', salonId)
    .in('status', ['trialing', 'active', 'past_due', 'incomplete', 'blocked', 'canceled'])
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  return mapSalonAbonnement(data, subRow, onlineBetalenFallbackToModule)
}

export async function updateSalonModules(
  salonId: string,
  modules: { modulePlanning: boolean; moduleKassa: boolean; moduleBetaling: boolean }
): Promise<void> {
  const { data: current } = await supabase
    .from('salons')
    .select('module_betaling')
    .eq('id', salonId)
    .maybeSingle()
  const enablingBetaling = modules.moduleBetaling && current?.module_betaling !== true

  const { error } = await supabase
    .from('salons')
    .update({
      module_planning: modules.modulePlanning,
      module_kassa: modules.moduleKassa,
      module_betaling: modules.moduleBetaling,
      abonnement_bevestigd_at: new Date().toISOString(),
      ...(enablingBetaling ? { online_betalen_bij_boeken: true } : {}),
    })
    .eq('id', salonId)
  if (error) {
    const status = (error as { status?: number }).status
    const msg = (error as { message?: string }).message ?? ''
    if (status === 400 || msg.includes('column')) {
      throw new Error('Database-migratie 007 (trial en modules) nog niet uitgevoerd. Voer supabase/migrations/007_trial_and_modules.sql uit in Supabase.')
    }
    throw error
  }
}

export async function getOnlineBetalenBijBoeken (salonId = DEFAULT_SALON_ID): Promise<boolean> {
  const { data, error } = await supabase
    .from('salons')
    .select('online_betalen_bij_boeken')
    .eq('id', salonId)
    .maybeSingle()
  if (error) {
    if (isMissingColumnError(error)) return false
    throw error
  }
  return data?.online_betalen_bij_boeken === true
}

export async function updateOnlineBetalenBijBoeken (salonId: string, enabled: boolean): Promise<void> {
  const { error } = await supabase
    .from('salons')
    .update({ online_betalen_bij_boeken: enabled })
    .eq('id', salonId)
  if (error) {
    const status = (error as { status?: number }).status
    const msg = (error as { message?: string }).message ?? ''
    if (status === 400 || msg.includes('column')) {
      throw new Error('Database-migratie 127 (online_betalen_bij_boeken) nog niet uitgevoerd.')
    }
    throw error
  }
}

export async function getSalonMollieUserFlowTestmode (salonId = DEFAULT_SALON_ID): Promise<boolean> {
  const { data, error } = await supabase
    .from('salons')
    .select('mollie_user_flow_testmode')
    .eq('id', salonId)
    .maybeSingle()
  if (error) {
    const status = (error as { status?: number }).status
    const msg = (error as { message?: string }).message ?? ''
    if (status === 400 || msg.includes('column')) return false
    throw error
  }
  return data?.mollie_user_flow_testmode === true
}

export async function updateSalonMollieUserFlowTestmode (salonId: string, enabled: boolean): Promise<void> {
  const { error } = await supabase
    .from('salons')
    .update({ mollie_user_flow_testmode: enabled })
    .eq('id', salonId)
  if (error) {
    const status = (error as { status?: number }).status
    const msg = (error as { message?: string }).message ?? ''
    if (status === 400 || msg.includes('column')) {
      throw new Error('Database-migratie 043 (mollie_user_flow_testmode) nog niet uitgevoerd.')
    }
    throw error
  }
}

function normalizeBookingDepositMode (raw: unknown): BookingDepositMode {
  return raw === 'fixed' || raw === 'percentage' || raw === 'none' ? raw : 'none'
}

export async function getBookingDepositSettings (salonId: string): Promise<BookingDepositSettings> {
  const { data, error } = await supabase
    .from('salons')
    .select('booking_deposit_mode, booking_deposit_fixed_amount, booking_deposit_percentage')
    .eq('id', salonId)
    .maybeSingle()
  if (error) {
    const status = (error as { status?: number }).status
    const msg = (error as { message?: string }).message ?? ''
    if (status === 400 || msg.includes('column')) {
      return { mode: 'none', fixedAmount: 0, percentage: 0 }
    }
    throw error
  }
  return {
    mode: normalizeBookingDepositMode(data?.booking_deposit_mode),
    fixedAmount: Number(data?.booking_deposit_fixed_amount ?? 0),
    percentage: Number(data?.booking_deposit_percentage ?? 0),
  }
}

export async function updateBookingDepositSettings (
  salonId: string,
  settings: BookingDepositSettings
): Promise<void> {
  const mode = normalizeBookingDepositMode(settings.mode)
  const fixedAmount = Math.max(0, Number(settings.fixedAmount) || 0)
  const percentage = Math.min(100, Math.max(0, Number(settings.percentage) || 0))
  const { error } = await supabase
    .from('salons')
    .update({
      booking_deposit_mode: mode,
      booking_deposit_fixed_amount: fixedAmount,
      booking_deposit_percentage: percentage,
    })
    .eq('id', salonId)
  if (error) {
    const status = (error as { status?: number }).status
    const msg = (error as { message?: string }).message ?? ''
    if (status === 400 || msg.includes('column')) {
      throw new Error('Database-migratie 045 (booking_deposit_*) nog niet uitgevoerd.')
    }
    throw error
  }
}

export interface SalonListItem {
  id: string
  slug: string | null
  businessName: string | null
  address: string | null
  activityCategoryIds: string[]
  bannerImageUrl?: string | null
}

export interface ActivityCategory {
  id: string
  naam: string
  volgorde: number
}

export async function getSalonBySlug(slug: string): Promise<{ id: string; slug: string | null } | null> {
  const { data, error } = await supabase
    .from('salons')
    .select('id, slug')
    .eq('slug', slug)
    .maybeSingle()
  if (error) throw error
  return data ? { id: data.id, slug: data.slug } : null
}

/** Uitkomst op basis van ingevoerd URL-pad, na `slugify` t.o.v. `salons.slug`. */
export type SalonSlugBeschikbaarheidUitslag =
  | 'leeg'
  | 'beschikbaar'
  | 'huidige_salon'
  | 'reeds_in_gebruik'

/**
 * @param urlPadInvoer Ruwe invoer (zoals in instellingen); zelfde normalisatie als bij opslaan.
 * @param huidigSalonId Huidige salon in instellingen; `null` bij nieuwe inschrijving (geen “eigen” conflict).
 */
export async function checkSalonSlugBeschikbaarheid (
  urlPadInvoer: string,
  huidigSalonId: string | null
): Promise<SalonSlugBeschikbaarheidUitslag> {
  if (!urlPadInvoer.trim()) return 'leeg'
  const candidate = slugify(urlPadInvoer)
  const bestaand = await getSalonBySlug(candidate)
  if (!bestaand) return 'beschikbaar'
  if (huidigSalonId != null && bestaand.id === huidigSalonId) return 'huidige_salon'
  return 'reeds_in_gebruik'
}

export async function getActivityCategories(): Promise<ActivityCategory[]> {
  const { data, error } = await supabase
    .from('activity_categories')
    .select('id, naam, volgorde')
    .order('volgorde', { ascending: true })
    .order('naam', { ascending: true })
  if (error) throw error
  return (data ?? []).map((r) => ({
    id: r.id,
    naam: r.naam,
    volgorde: r.volgorde ?? 999,
  }))
}

export async function getSalons(opts?: { activityCategoryId?: string }): Promise<SalonListItem[]> {
  let query = supabase
    .from('salons')
    .select('id, slug, business_name, address, banner_image_url')
    .not('slug', 'is', null)
    .eq('list_on_marketplace', true)

  if (opts?.activityCategoryId) {
    const { data: links } = await supabase
      .from('salon_activity_categories')
      .select('salon_id')
      .eq('activity_category_id', opts.activityCategoryId)
    const salonIds = (links ?? []).map((l) => l.salon_id)
    if (salonIds.length === 0) return []
    query = query.in('id', salonIds)
  }

  const { data: salons, error } = await query
  if (error) throw error
  if (!salons?.length) return []

  const { data: links } = await supabase
    .from('salon_activity_categories')
    .select('salon_id, activity_category_id')
    .in('salon_id', salons.map((s) => s.id))

  const map = new Map<string, string[]>()
  for (const l of links ?? []) {
    const arr = map.get(l.salon_id) ?? []
    arr.push(l.activity_category_id)
    map.set(l.salon_id, arr)
  }

  return salons.map((s) => ({
    id: s.id,
    slug: s.slug,
    businessName: s.business_name,
    address: s.address,
    activityCategoryIds: map.get(s.id) ?? [],
    bannerImageUrl: (s as { banner_image_url?: string | null }).banner_image_url ?? null,
  }))
}

export async function createSalonSelfService(
  slug: string,
  businessName: string,
  activityCategoryIds: string[]
): Promise<string> {
  const { data, error } = await supabase.rpc('create_salon_self_service', {
    p_slug: slug,
    p_business_name: businessName,
    p_activity_category_ids: activityCategoryIds,
  })
  if (error) {
    const err = error as { message?: string; details?: string; hint?: string }
    const msg = err.message || err.details || err.hint || 'Salon aanmaken mislukt'
    if (import.meta.env.DEV) console.error('[createSalonSelfService]', error)
    throw new Error(msg)
  }
  return String(data)
}

export async function getSalonActivityCategoryIds(salonId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('salon_activity_categories')
    .select('activity_category_id')
    .eq('salon_id', salonId)
  if (error) throw error
  return (data ?? []).map((r) => r.activity_category_id)
}

export async function updateSalonActivityCategories(
  salonId: string,
  activityCategoryIds: string[]
): Promise<void> {
  const { error: delError } = await supabase
    .from('salon_activity_categories')
    .delete()
    .eq('salon_id', salonId)
  if (delError) throw delError

  if (activityCategoryIds.length === 0) return

  const rows = activityCategoryIds.map((activity_category_id) => ({
    salon_id: salonId,
    activity_category_id,
  }))
  const { error: insertError } = await supabase
    .from('salon_activity_categories')
    .insert(rows)
  if (insertError) throw insertError
}
